import React from 'react'
import { IMkStepperContext, MkApiContext } from '@typescript'
import { IBookRentalOptions, ICost, IMkAnyBookField, IMkBookState, IMkBookStepTravelerInfo, IMkBookSummary, IMkBookTimerData, MkModifyBookStep } from '@features/book/types'
import { IBookService } from '@features/book/services/bookService/IBookService'

export interface MkBookContextType extends IMkStepperContext, Omit<IMkBookState, 'errors'> {
  bookService: IBookService
  bookingErrors?: IMkBookState['bookingErrors']
  membershipCharges: IMkBookSummary['membershipCharges']
  offerCodeEligible: boolean
  paymentCharges: IMkBookSummary['paymentCharges']
  memberReimbursements: IMkBookSummary['reimbursements']['member']
  paymentReimbursements: IMkBookSummary['reimbursements']['payment']
  totalPaymentDue: number
  stay: IMkBookSummary['stay']
  resort: IMkBookSummary['resort']
  vipUpgradeNowFields: IMkAnyBookField[]
  vipUpgradeFutureFields: IMkAnyBookField[]
  rentalOptions: IBookRentalOptions
  cancellationExpirationDate: string
  cancellationPenaltyDeadlineLapsed: boolean
  hasUpdatedModifySteps: boolean
  findStepIndexByName(stepName: string): number
  getBookingTimerData(): IMkBookTimerData | undefined
  getCostById: (costId: string) => ICost
  getCostLabel: (costId: string) => string
  getCreditsDeficit: (userCreditsBalance?: number) => number
  getFormattedCostAmount: (costId: string) => string
  getGuestConfirmationCost: () => number
  getPointsDeficit: (userPointsBalance: number) => number
  getPurchaseGuestConfirmationCost: () => number
  getReservationPointsDeficit: () => number
  getStayCreditsCost: () => number
  getStayPointsCost: () => number
  getTravelerLabel: (step: IMkBookStepTravelerInfo) => string | undefined
  initBooking: (params: {
    resort: IMkBookSummary['resort']
    suite: IMkBookSummary['stay']['suite']
    checkInDate: IMkBookSummary['stay']['checkIn']
    checkOutDate: IMkBookSummary['stay']['checkOut']
    vgerResortAvail?: any
  }) => () => Promise<void>
  initModification: (params: { tripId: string; context?: MkApiContext | undefined; step?: MkModifyBookStep }) => Promise<void>
  isInModificationMode: () => boolean
  initBookingTimer(durationMinutes: number, startTime?: number): void
  initialState: () => IMkBookState
  requiresAdditionalPayment?: boolean
  setRequiresAdditionalPayment: (requiresAdditionalPayment: boolean) => void
  setBookingError: ({ raw, message }: { raw?: Error; message?: string }) => void
  setBookingErrors: (errors: Array<{ message?: string; raw?: Error } | string>) => void
  clearBookingErrors: () => void
  setApiContext: (context: MkApiContext) => void
  getApiContext: () => MkApiContext
  stopBookingTimer(): void
  clearBookingTimer(): void
  updatePointSliderValues: (totalAmount: number, leavingValue: number, input1: IMkAnyBookField, input2: IMkAnyBookField) => void
  updateBookingTimer(remainingSeconds: number, isLastTwoMinutes: boolean, isTimeUp: boolean): void
  cancelBooking: () => Promise<void>
  cancelModifyReservation: () => Promise<void>
  getUserBalanceById: (balanceId: string) => number
  updateDatesStepContent: (dates: { earliestCheckIn?: string; latestCheckOut?: string; excluded?: string[] }) => void
  updateBookDraft: (bookDraft: any) => void
}

export const MkBookContext = React.createContext<MkBookContextType | undefined>(undefined)
